import React from 'react';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Input } from '@osmosis/tailwind-ui/components/Input';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text3, text5 } from '@osmosis/tailwind-ui/styles/text';
import { bg1, borderNeutral200 } from '@osmosis/tailwind-ui/styles';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { useIntl } from 'react-intl';

type Props = {
  search: string;
  query: string;
  matchCount: number;
  searchCountLabel: string;
  isLocked: boolean;
  isSearchBarStickable: boolean;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onNavigate: (direction: 1 | -1) => void;
  onClear: () => void;
};

/** Transcript search field with match counter, prev/next nav, and clear. */
export default function SearchBar({
  search,
  query,
  matchCount,
  searchCountLabel,
  isLocked,
  isSearchBarStickable,
  onChange,
  onKeyDown,
  onNavigate,
  onClear,
}: Props) {
  const intl = useIntl();

  return (
    <Box
      data-testid="captions-search-wrapper"
      className={cn(
        'relative w-full shrink-0 border-b',
        bg1,
        borderNeutral200,
        isSearchBarStickable && 'sticky top-[50px] z-20 shadow-md',
      )}
    >
      <Icon
        size="sm"
        icon={faMagnifyingGlass}
        className={cn('absolute left-3 top-3', text5)}
      />
      <Input
        data-testid="captions-search"
        type="text"
        value={search}
        disabled={isLocked}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={intl.formatMessage({
          id: 'learn.captions_transcript.search_placeholder',
          defaultMessage: 'Search transcript',
        })}
        className="w-full rounded-none bg-neutral-100 py-2 pl-9 pr-28 text-sm"
      />
      <Flex className="absolute right-3 top-1.5 items-center gap-1">
        {!!query && (
          <P
            data-testid="captions-search-count"
            cn={cn('!text-xs font-semibold whitespace-nowrap', text3)}
          >
            {searchCountLabel}
          </P>
        )}
        {matchCount > 0 && (
          <Flex
            className={cn(
              'items-center gap-1 border-l pl-1.5 ml-0.5',
              borderNeutral200,
            )}
          >
            <IconButton
              variant="transparent"
              data-testid="captions-search-nav-prev"
              icon={<Icon icon={faChevronUp} size="xs" />}
              ariaLabel={intl.formatMessage({
                id: 'learn.captions_transcript.previous_match',
                defaultMessage: 'Previous match',
              })}
              onClick={() => onNavigate(-1)}
              cn="!h-auto !w-auto !p-1"
            />
            <IconButton
              variant="transparent"
              data-testid="captions-search-nav-next"
              icon={<Icon icon={faChevronDown} size="xs" />}
              ariaLabel={intl.formatMessage({
                id: 'learn.captions_transcript.next_match',
                defaultMessage: 'Next match',
              })}
              onClick={() => onNavigate(1)}
              cn="!h-auto !w-auto !p-1"
            />
          </Flex>
        )}
        {!!search && (
          <IconButton
            variant="transparent"
            data-testid="captions-search-clear"
            icon={<Icon icon={faXmark} size="xs" />}
            ariaLabel={intl.formatMessage({
              id: 'learn.captions_transcript.clear_search',
              defaultMessage: 'Clear search',
            })}
            onClick={onClear}
            cn="!h-auto !w-auto !p-1"
          />
        )}
      </Flex>
    </Box>
  );
}
